import { PlatformError, type DocTxOps } from '@djgg/core';
import { canonicalDigest } from '@djgg/gameplay-kit';
import { buildJobDoc, qualityOutboxDoc, qualityPlatformControlDoc, qualityRunDoc } from './paths.js';
import { assertQualityOperationAllowed, validateQualityPlatformControlV1, type QualityPlatformControlV1 } from './qualityControl.js';
import { validateQualityAdmissionV2, type QualityAdmissionV2 } from './qualitySchemas.js';
import type { QualityRunRecordV2 } from './repos/qualityRuns.js';
import type { QualityOutboxRecordV1 } from './repos/qualityOutbox.js';
import type { BuildJob } from './types.js';

export interface QualityAdmissionWriteV2 {
  admission: QualityAdmissionV2;
  initialOutboxId: string;
  controlRevision: number;
}

/** Fence both legacy and V2 acceptance against the rollout snapshot selected
 * by the handler. This prevents an in-flight legacy write crossing an Admin
 * enable boundary (and the inverse) under the same durable transaction. */
export async function assertQualityControlRevisionV1(
  tx: DocTxOps,
  expectedRevision: number | null | undefined,
): Promise<void> {
  if (expectedRevision === undefined) return;
  const stored = await tx.get<QualityPlatformControlV1>(qualityPlatformControlDoc());
  if (expectedRevision === null) {
    if (stored) throw new PlatformError('conflict', 'quality control appeared during acceptance');
    return;
  }
  if (!stored) throw new PlatformError('conflict', 'quality control disappeared during acceptance');
  const control = validateQualityPlatformControlV1(stored);
  if (control.revision !== expectedRevision) {
    throw new PlatformError('conflict', 'quality control changed during acceptance');
  }
  // Off is a valid legacy-selection snapshot; pause blocks only an active mode.
  if (control.admissionMode !== 'off' && control.acceptancePaused) {
    throw new PlatformError('unavailable', 'quality acceptance is paused');
  }
}

export async function writeQualityAdmissionV2(
  tx: DocTxOps,
  value: QualityAdmissionWriteV2,
  expected: {
    jobId: string;
    creationId: string;
    version: number;
    commandId: string;
    executionKind: string;
    baseVersion: number;
  },
  now: number,
): Promise<{ runId: string; admissionDigest: string }> {
  const admission = validateQualityAdmissionV2(value.admission);
  if (admission.creationId !== expected.creationId
    || admission.version !== expected.version
    || admission.commandId !== expected.commandId
    || admission.executionKind !== expected.executionKind
    || admission.lineage.baseVersion !== expected.baseVersion) {
    throw new PlatformError('validation', 'quality admission does not match the locked acceptance authority');
  }
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(value.initialOutboxId)) {
    throw new PlatformError('validation', 'quality admission requires a stable initial outbox id');
  }
  if (value.initialOutboxId !== `${admission.runId}:compile-specification`) {
    throw new PlatformError('validation', 'quality admission requires the canonical specification outbox identity');
  }
  const admissionDigest = await canonicalDigest(admission);
  const runPath = qualityRunDoc(admission.runId);
  const job = await tx.get<BuildJob>(buildJobDoc(expected.jobId));
  if (!job || job.creationId !== admission.creationId || job.version !== admission.version
    || job.requiredWorkerCapability !== 'verified-core-v2') {
    throw new PlatformError('conflict', 'quality admission requires its exact verified-core-v2 build job');
  }
  const existing = await tx.get<QualityRunRecordV2>(runPath);
  if (existing) {
    if (existing.jobId !== expected.jobId || existing.commandId !== admission.commandId
      || existing.admissionDigest !== admissionDigest) {
      throw new PlatformError('conflict', 'quality run identity was reused with different accepted authority');
    }
    return { runId: admission.runId, admissionDigest };
  }
  const control = await tx.get<QualityPlatformControlV1>(qualityPlatformControlDoc());
  if (!control) throw new PlatformError('unavailable', 'quality control is not initialized');
  assertQualityOperationAllowed(control, 'acceptance', {
    revision: value.controlRevision,
    policyRevision: admission.policyRevision,
  });
  const run: QualityRunRecordV2 = {
    runId: admission.runId,
    jobId: expected.jobId,
    commandId: admission.commandId,
    creationId: admission.creationId,
    version: admission.version,
    admission,
    admissionDigest,
    executionCheckpoint: {},
    state: {
      schemaVersion: 2,
      runId: admission.runId,
      revision: 0,
      phase: 'contract-pending',
      basisEpoch: 0,
      operationAttempt: 0,
      durableDeadline: null,
    },
    createdAt: now,
    updatedAt: now,
  };
  const outbox: QualityOutboxRecordV1 = {
    id: value.initialOutboxId,
    runId: admission.runId,
    kind: 'compile-specification',
    status: 'pending',
    payload: { runId: admission.runId, admissionDigest },
    claimToken: null,
    leaseGeneration: 0,
    leaseExpiry: null,
    availableAt: now,
    createdAt: now,
    finishedAt: null,
    error: null,
  };
  await tx.set(runPath, run as unknown as Record<string, unknown>);
  await tx.set(qualityOutboxDoc(value.initialOutboxId), outbox as unknown as Record<string, unknown>);
  return { runId: admission.runId, admissionDigest };
}
